import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { tags as t } from '@lezer/highlight';

/**
 * Structural CSS for the EditorView. Body colours inherit from the outer
 * container (which sets bg-default / text-default via Tailwind), so light/dark
 * mode follows the design-tokens layer automatically.
 */
const codeEditorTheme = EditorView.theme({
  '&': {
    color: 'inherit',
    backgroundColor: 'transparent',
    fontFamily: 'ui-monospace, Menlo, monospace',
    // Fill the flex parent (the outer wrapper sets display:flex). minHeight:0
    // is required so this flex item can shrink below content size and let
    // .cm-scroller below do the internal scrolling instead of the editor
    // pushing past the wrapper's max-height.
    flex: '1 1 0',
    minHeight: '0',
  },
  '.cm-scroller': {
    overflow: 'auto',
  },
  '.cm-content': {
    color: 'inherit',
    caretColor: 'currentColor',
    // Explicit user-select to defend against any inherited utility/reset that
    // might disable selection on the contenteditable.
    userSelect: 'text',
    WebkitUserSelect: 'text',
  },
  // The token resolves to a hex literal with alpha (#00000026), so we use
  // `var(...)` directly rather than wrapping in `rgb(... / x)` (which expects
  // RGB channel values, not a hex string — that would silently produce invalid
  // CSS and the browser would drop the rule).
  '.cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: 'var(--semantic-colors-interaction-press)',
  },
  '&.cm-focused': {
    outline: 'none',
  },
  // Line-number / fold gutter chrome. Background stays transparent so the
  // outer container colour shows through; foreground + right border come from
  // --cm-gutter-* tokens (light/dark variants live in CodeEditor.styles.ts).
  // Default CM gutter sits flush with the editor border and presses fold
  // markers against the code — pad both sides so the column has breathing
  // room, and add a right gap on line-numbers to separate them from the
  // fold-gutter column.
  '.cm-gutters': {
    backgroundColor: 'transparent',
    color: 'var(--cm-gutter-fg)',
    borderRight: '1px solid var(--cm-gutter-border)',
    paddingLeft: '0.5rem',
    paddingRight: '0.25rem',
  },
  '.cm-lineNumbers .cm-gutterElement': {
    paddingRight: '0.375rem',
  },
  // Centre fold markers in their column so `▾` and `▸` (different glyph
  // widths) line up the same on every row.
  '.cm-foldGutter .cm-gutterElement': {
    textAlign: 'center',
    minWidth: '0.875rem',
  },
  // Active-line highlight is opt-in via the `highlightActiveLine` prop, so
  // these rules are no-ops when the prop is off. Tokens with light/dark
  // variants live in CodeEditor.styles.ts — the dark literals are kept close
  // to the container background to avoid a glaring stripe.
  '.cm-activeLine': {
    backgroundColor: 'var(--cm-active-line-bg)',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'var(--cm-active-line-gutter-bg)',
  },
});

/**
 * Single HighlightStyle shared across all languages. Colours are pulled from
 * `--cm-syntax-*` custom properties set on the outer container (see
 * CodeEditor.styles.ts), which carry `dark:` variants — so syntax colours
 * follow the app's light/dark mode automatically.
 *
 * Tag coverage spans JSON, Jinja2 and Mustache-over-HTML. The Mustache mode
 * delegates to the legacy html parser for content outside `{{...}}`, so the
 * style block also covers HTML token tags (tagName, attributeName,
 * angleBracket).
 */
const codeEditorHighlightStyle = HighlightStyle.define([
  { tag: t.comment, color: 'var(--cm-syntax-comment)', fontStyle: 'italic' },
  { tag: t.keyword, color: 'var(--cm-syntax-keyword)' },
  { tag: t.string, color: 'var(--cm-syntax-string)' },
  { tag: t.number, color: 'var(--cm-syntax-number)' },
  { tag: t.bool, color: 'var(--cm-syntax-number)' },
  { tag: t.null, color: 'var(--cm-syntax-number)' },
  { tag: t.atom, color: 'var(--cm-syntax-number)' },
  { tag: t.variableName, color: 'var(--cm-syntax-number)' },
  { tag: t.propertyName, color: 'var(--cm-syntax-property)' },
  { tag: t.attributeName, color: 'var(--cm-syntax-property)' },
  { tag: t.tagName, color: 'var(--cm-syntax-keyword)' },
  { tag: t.angleBracket, color: 'var(--cm-syntax-punctuation)' },
  { tag: t.punctuation, color: 'var(--cm-syntax-punctuation)' },
  { tag: t.meta, color: 'var(--cm-syntax-comment)' },
  { tag: t.invalid, color: 'var(--cm-syntax-keyword)' },
]);

export const codeEditorThemeExtensions: Extension = [
  codeEditorTheme,
  syntaxHighlighting(codeEditorHighlightStyle),
];
